import React, { useMemo } from 'react';
import { TotalsCell } from './TotalsCell';
import { DefaultCell } from './DefaultCell';

/**
 * Hook to transform header definitions into react-data-grid column definitions
 * 
 * @param {Array} headers - Header definitions with properties like text, valueFn, cellType, etc.
 * @param {Object} totalsRow - Optional hash mapping header text to totals value
 * @returns {Array} Transformed column definitions for react-data-grid
 */
export function useTransformHeadersToColumns(headers, totalsRow) {
  return useMemo(() => {
    return headers.map((header, index) => {
      // If header doesn't have renderCell, provide default
      const defaultRenderCell = _ref => {
        let {
          row,
          column,
          tabIndex
        } = _ref;
        // Totals row takes priority
        if (row.isTotalsRow) {
          return /*#__PURE__*/React.createElement(TotalsCell, {
            headerText: header.text,
            totalsRow: column.totalsRow
          });
        }

        // Default cell with valueFn
        const wrappedRow = {
          data: {
            object: row
          }
        };
        const displayValue = header.valueFn ? header.valueFn(wrappedRow) : '';

        // Check if cell is editable (use editable function if available, otherwise check cellType/disabled)
        const isEditable = column.editable ? column.editable(row) : header.cellType && !header.disabled;
        return /*#__PURE__*/React.createElement(DefaultCell, {
          header: header,
          value: displayValue,
          isEditable: isEditable,
          tabIndex: tabIndex
        });
      };
      return {
        key: `col_${index}`,
        name: header.text,
        resizable: true,
        totalsRow,
        // Pass totalsRow to each column so renderCell can use it
        ...header,
        // Spread all header properties directly onto column
        renderCell: header.renderCell || defaultRenderCell // Use header's renderCell or default
      };
    });
  }, [headers, totalsRow]);
}